import type { JobPostingRead } from '@/features/recruitment/api'

/**
 * "Post this job" links for `JobPostings.tsx` (recruitment marketing, no API integration).
 *
 * Every one of these is a plain share-intent URL the platform itself defines -- opening it
 * hands off to the platform's own compose screen, pre-filled if the user is already logged
 * in there. Nothing here talks to Facebook/LinkedIn/etc.; there is no token, no posting API.
 *
 * Facebook and LinkedIn's share endpoints only accept a URL -- they pull the preview title
 * and description from that page's own Open Graph meta tags (see
 * `StorefrontCareerDetail.tsx`'s `useDocumentMeta` call), not from anything passed here.
 */

export type ShareTarget = 'facebook' | 'linkedin' | 'twitter' | 'whatsapp' | 'email'

export const SHARE_TARGET_LABELS: Record<ShareTarget, string> = {
  facebook: 'Share on Facebook',
  linkedin: 'Share on LinkedIn',
  twitter: 'Share on X',
  whatsapp: 'Share on WhatsApp',
  email: 'Share by email',
}

function careerPostingUrl(postingId: string): string {
  return `${window.location.origin}/store/careers/${postingId}`
}

function pitchLine(posting: Pick<JobPostingRead, 'title' | 'department_name'>): string {
  return posting.department_name
    ? `We're hiring: ${posting.title} (${posting.department_name}).`
    : `We're hiring: ${posting.title}.`
}

/** The URL to `window.open()` for a given platform and posting. */
export function shareUrlFor(
  target: ShareTarget,
  posting: Pick<JobPostingRead, 'id' | 'title' | 'department_name'>,
): string {
  const link = careerPostingUrl(posting.id)
  const text = `${pitchLine(posting)} Apply here: ${link}`

  switch (target) {
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`
    case 'twitter':
      return `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        pitchLine(posting),
      )}&url=${encodeURIComponent(link)}`
    case 'whatsapp':
      return `https://wa.me/?text=${encodeURIComponent(text)}`
    case 'email':
      return `mailto:?subject=${encodeURIComponent(
        `We're hiring: ${posting.title}`,
      )}&body=${encodeURIComponent(text)}`
  }
}
